/**
 * Entity Type Constants
 *
 * Maps frontend entity names to backend entityType values used in activities, comments, etc.
 * Backend uses consistent snake_case from EntityTypes constants.
 *
 * Source: moly-api/src/Moly/Domain/Constants/EntityTypes.cs
 */

/**
 * Backend entity types (snake_case)
 * Used consistently across all backend systems
 */
export const ENTITY_TYPES = {
  WORK_ITEM: 'work_item',
  PROJECT: 'project',
  COMMENT: 'comment',
  WORKSPACE: 'workspace',
  USER: 'user',
  DOCUMENT: 'document',
  PERSONAL_NOTE: 'personal_note',
  WORK_ITEM_LIST: 'work_item_list',
  NOTIFICATION: 'notification',
  REMINDER: 'reminder',
} as const

/**
 * Frontend entity names (for reference)
 * These map to backend entities but are used in collection names and UI
 */
export const FRONTEND_ENTITIES = {
  TASK: 'task',
  TASK_CONTENT: 'taskContent',
  COMMENT: 'comment',
  PROJECT: 'project',
  LABEL: 'label',
  CHECKLIST: 'checklist',
  CHECKLIST_ITEM: 'checklistItem',
  PAGE: 'page',
  PAGE_CONTENT: 'pageContent',
  PERSONAL_NOTE: 'personalNote',
  PERSONAL_NOTE_CONTENT: 'personalNoteContent',
} as const

/**
 * Helper to get backend entity type for a given frontend entity
 */
/**
 * Entities that can be linked to/from via the backlinks system.
 * Maps a display label to the backend entity type constant.
 */
export const LINKABLE_ENTITY_TYPES = [
  ENTITY_TYPES.WORK_ITEM,
  ENTITY_TYPES.DOCUMENT,
  ENTITY_TYPES.PROJECT,
] as const

export type LinkableEntityType = (typeof LINKABLE_ENTITY_TYPES)[number]

/**
 * Display labels for linkable entity types
 */
export const LINKABLE_ENTITY_LABELS: Record<LinkableEntityType, string> = {
  [ENTITY_TYPES.WORK_ITEM]: 'Task',
  [ENTITY_TYPES.DOCUMENT]: 'Page',
  [ENTITY_TYPES.PROJECT]: 'Project',
} as const

export function getBackendEntityType(frontendEntity: string): string {
  switch (frontendEntity) {
    case FRONTEND_ENTITIES.TASK:
      return ENTITY_TYPES.WORK_ITEM
    case FRONTEND_ENTITIES.COMMENT:
      return ENTITY_TYPES.COMMENT
    case FRONTEND_ENTITIES.PROJECT:
      return ENTITY_TYPES.PROJECT
    case FRONTEND_ENTITIES.PAGE:
      return ENTITY_TYPES.DOCUMENT
    default:
      return frontendEntity
  }
}
